/**
 * EventPlannerBookingFlow - Consultation booking for Event Planners
 * Package tiers with consultation scheduling
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { 
  Calendar as CalendarIcon, Clock, ClipboardList, Check, ChevronRight, Loader2,
  Phone, Video, Users, Briefcase, Star, MessageSquare
} from 'lucide-react';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Package tiers
const PLANNER_PACKAGES = [
  {
    id: 'consultation',
    name: 'Initial Consultation',
    price_uk: 75,
    price_sl: 7500,
    duration: '1 hour',
    icon: MessageSquare,
    description: 'One-on-one consultation to discuss your event vision',
    includes: ['Needs assessment', 'Budget guidance', 'Vendor recommendations', 'Follow-up email summary'],
    is_consultation: true
  },
  {
    id: 'partial',
    name: 'Partial Planning',
    price_uk: 500,
    price_sl: 75000,
    duration: 'Per event',
    icon: ClipboardList,
    description: 'We handle specific aspects while you manage the rest',
    includes: ['Vendor sourcing & negotiations', 'Timeline creation', 'Design consultation', '2 site visits', 'Email support'],
    popular: true
  },
  {
    id: 'full',
    name: 'Full Planning',
    price_uk: 1500,
    price_sl: 250000,
    duration: 'Per event',
    icon: Briefcase,
    description: 'Complete end-to-end event planning and coordination',
    includes: ['All Partial Planning features', 'Budget management', 'Guest list management', 'Day-of coordination', 'Unlimited consultations', 'Rehearsal coordination'],
    premium: true
  },
  {
    id: 'day_of',
    name: 'Day-of Coordination',
    price_uk: 350,
    price_sl: 45000,
    duration: '10 hours',
    icon: Users,
    description: 'We manage everything on your event day',
    includes: ['Final vendor confirmations', 'Event day timeline', 'Setup supervision', 'Vendor coordination', 'Emergency handling', 'Up to 10 hours coverage']
  }
];

// Time slots for consultations
const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'
];

// Consultation types
const CONSULTATION_TYPES = [
  { id: 'video', name: 'Video Call', icon: Video, description: 'Via Zoom or Google Meet' },
  { id: 'phone', name: 'Phone Call', icon: Phone, description: 'We\'ll call you' },
  { id: 'in_person', name: 'In Person', icon: Users, description: 'At our office or venue' }
];

// Event types for context
const EVENT_TYPES = [
  'Wedding', 'Corporate Event', 'Birthday Party', 'Engagement', 'Anniversary',
  'Baby Shower', 'Conference', 'Gala Dinner', 'Product Launch', 'Other'
];

export default function EventPlannerBookingFlow({ open, onClose, provider, onBookingComplete }) {
  const [step, setStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [consultationType, setConsultationType] = useState('video');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [eventType, setEventType] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [budget, setBudget] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const country = provider?.country || 'SL';
  const currency = country === 'UK' ? '£' : 'Rs';

  const getPackagePrice = (pkg) => {
    return country === 'UK' ? pkg.price_uk : pkg.price_sl;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingData = {
        provider_id: provider?.id,
        package_id: selectedPackage?.id,
        package_name: selectedPackage?.name,
        package_price: getPackagePrice(selectedPackage),
        consultation_type: selectedPackage?.is_consultation ? consultationType : null,
        consultation_date: selectedPackage?.is_consultation && selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
        consultation_time: selectedPackage?.is_consultation ? selectedTime : null,
        event_type: eventType,
        tentative_event_date: eventDate,
        expected_guests: guestCount ? parseInt(guestCount) : null,
        budget_range: budget,
        event_description: eventDescription,
        contact: contactInfo,
        country: country
      };

      const response = await fetch(`${API_URL}/api/events/bookings/planner`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        const successMsg = selectedPackage?.is_consultation 
          ? 'Consultation booked! You\'ll receive confirmation shortly.'
          : 'Inquiry sent! The planner will contact you within 24 hours.';
        toast.success(successMsg);
        onBookingComplete?.();
        onClose();
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      toast.error('Failed to complete booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Select a Package</h3>
            <p className="text-sm text-gray-500">Choose the level of planning support you need</p>

            <div className="space-y-3">
              {PLANNER_PACKAGES.map(pkg => {
                const Icon = pkg.icon;
                const price = getPackagePrice(pkg);
                const isSelected = selectedPackage?.id === pkg.id;

                return (
                  <button
                    key={pkg.id}
                    onClick={() => setSelectedPackage(pkg)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all relative ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    {pkg.popular && (
                      <Badge className="absolute -top-2 right-4 bg-blue-600">Most Popular</Badge>
                    )}
                    {pkg.premium && (
                      <Badge className="absolute -top-2 right-4 bg-gradient-to-r from-purple-600 to-pink-600">
                        <Star className="w-3 h-3 mr-1" /> Premium
                      </Badge>
                    )}

                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-bold">{pkg.name}</p>
                          <p className="font-bold text-blue-600">{currency} {price.toLocaleString()}</p>
                        </div>
                        <p className="text-xs text-gray-500">{pkg.duration}</p>
                        <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>
                        
                        <ul className="mt-2 grid grid-cols-2 gap-1">
                          {pkg.includes.map((item, idx) => (
                            <li key={idx} className="text-xs text-gray-500 flex items-center gap-1">
                              <Check className="w-3 h-3 text-green-500" /> {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 2:
        if (selectedPackage?.is_consultation) {
          return (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Schedule Your Consultation</h3>

              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Consultation Type</label>
                <RadioGroup value={consultationType} onValueChange={setConsultationType} className="grid grid-cols-3 gap-3">
                  {CONSULTATION_TYPES.map(type => {
                    const Icon = type.icon;
                    return (
                      <div key={type.id}>
                        <RadioGroupItem value={type.id} id={type.id} className="peer sr-only" />
                        <Label
                          htmlFor={type.id}
                          className="flex flex-col items-center p-3 border rounded-lg cursor-pointer peer-data-[state=checked]:border-blue-500 peer-data-[state=checked]:bg-blue-50"
                        >
                          <Icon className="w-5 h-5 mb-1" />
                          <span className="text-sm font-medium">{type.name}</span>
                          <span className="text-xs text-gray-500">{type.description}</span>
                        </Label>
                      </div>
                    );
                  })}
                </RadioGroup>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Select Date</label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const day = date.getDay();
                      return date < new Date() || day === 0 || day === 6; // No past dates, weekends
                    }}
                    className="rounded-md border"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Select Time</label>
                  <div className="grid grid-cols-2 gap-2">
                    {TIME_SLOTS.map(time => (
                      <button
                        key={time}
                        onClick={() => setSelectedTime(time)}
                        className={`p-2 rounded-lg border text-sm font-medium transition-all ${
                          selectedTime === time
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'hover:border-blue-300'
                        }`}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {selectedDate && selectedTime && (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-green-800">
                    <Clock className="w-4 h-4 inline mr-2" />
                    {format(selectedDate, 'EEEE, MMMM d, yyyy')} at {selectedTime}
                  </p>
                </div>
              )}
            </div>
          );
        } else {
          return (
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-800">Tell Us About Your Event</h3>
              <p className="text-sm text-gray-500">Help us understand your needs so we can provide the best service</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Event Type</label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Tentative Date</label>
                  <Input
                    type="date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    min={format(addDays(new Date(), 30), 'yyyy-MM-dd')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Expected Guests</label>
                  <Input
                    type="number"
                    value={guestCount}
                    onChange={(e) => setGuestCount(e.target.value)}
                    placeholder="e.g., 150"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Budget Range</label>
                  <Select value={budget} onValueChange={setBudget}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {country === 'UK' ? (
                        <>
                          <SelectItem value="under_5k">Under £5,000</SelectItem>
                          <SelectItem value="5k_10k">£5,000 - £10,000</SelectItem>
                          <SelectItem value="10k_25k">£10,000 - £25,000</SelectItem>
                          <SelectItem value="25k_50k">£25,000 - £50,000</SelectItem>
                          <SelectItem value="over_50k">Over £50,000</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="under_500k">Under Rs. 500,000</SelectItem>
                          <SelectItem value="500k_1m">Rs. 500,000 - 1 Million</SelectItem>
                          <SelectItem value="1m_2.5m">Rs. 1 - 2.5 Million</SelectItem>
                          <SelectItem value="2.5m_5m">Rs. 2.5 - 5 Million</SelectItem>
                          <SelectItem value="over_5m">Over Rs. 5 Million</SelectItem>
                        </>
                      )}
                      <SelectItem value="flexible">Flexible / Discuss</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">Tell Us About Your Vision</label>
                <Textarea
                  value={eventDescription}
                  onChange={(e) => setEventDescription(e.target.value)}
                  placeholder="Describe your dream event - theme, style, specific requirements..."
                  rows={4}
                />
              </div>
            </div>
          );
        }

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Your Contact Details</h3>

            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium">Booking Summary</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Package</span>
                  <span className="font-medium">{selectedPackage?.name}</span>
                </div>
                {selectedPackage?.is_consultation && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Type</span>
                      <span className="capitalize">{consultationType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Date & Time</span>
                      <span>
                        {selectedDate && format(selectedDate, 'MMM d, yyyy')} at {selectedTime}
                      </span>
                    </div>
                  </>
                )}
                {eventType && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Event</span>
                    <span>{eventType}</span>
                  </div>
                )}
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>{selectedPackage?.is_consultation ? 'Consultation Fee' : 'Starting From'}</span>
                  <span className="text-blue-600">
                    {currency} {getPackagePrice(selectedPackage).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <ClipboardList className="w-4 h-4 inline mr-2" />
                {selectedPackage?.is_consultation 
                  ? 'You\'ll receive a calendar invite with meeting details within 24 hours.'
                  : 'The event planner will contact you within 24 hours to discuss your event in detail.'}
              </p>
            </div>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            Event Planning Services
          </DialogTitle>
          <DialogDescription>{provider.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-blue-600 text-white' :
                s === step ? 'bg-blue-100 text-blue-700 border-2 border-blue-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 ${s < step ? 'bg-blue-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}

          {step < 3 ? (
            <Button
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !selectedPackage) ||
                (step === 2 && selectedPackage?.is_consultation && (!selectedDate || !selectedTime))
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {selectedPackage?.is_consultation ? 'Book Consultation' : 'Send Inquiry'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
